import { CSSObject } from '@emotion/react'
import { Color, Overlay, Transition, ZIndex } from '@opallabs/gem'
import { modernWithFallback } from '@opallabs/lambda'
import { WithRequiredTheme } from '@opallabs/lapidary'
import { ControlBarStyleProps } from '@opallabs/text-editor/src/TextEditor/components/ControlBar/types'

type FullScreenStyleProps = { isFullScreen: boolean }
type ViewModeWrapperWidth = { isNarrow: boolean }
type ContainerStyleProps = WithRequiredTheme & FullScreenStyleProps

// `buttonGroups` block padding * 2 + `button` block size + `controlBar` border
export const CONTROL_BAR_BLOCK_SIZE = 53

export const controlBar = ({
  isFullScreen,
  isVisible,
  theme,
}: ControlBarStyleProps & ContainerStyleProps): CSSObject => {
  const base: CSSObject = {
    position: 'sticky',
    insetBlockEnd: -CONTROL_BAR_BLOCK_SIZE,
    ...modernWithFallback(
      [
        { overflowBlock: 'visible', overflowInline: 'hidden' },
        { overflowY: 'visible', overflowX: 'hidden' },
      ],
      'overflow-block: auto'
    ),

    blockSize: 'auto',
    maxBlockSize: 0,
    opacity: 0,
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    alignItems: 'center',
    borderBlockStart: '1px solid transparent',
    borderStartStartRadius: '0',
    borderStartEndRadius: '0',
    borderEndEndRadius: '12px',
    borderEndStartRadius: '12px',
    pointerEvents: 'none',
    zIndex: ZIndex.Element.Level1,
    ...Transition(['border-top-color', 'max-height', 'opacity']),

    '> div': {
      display: 'flex',
      paddingInline: '16px',
    },
  }

  const visibleStyle: CSSObject = {
    maxBlockSize: CONTROL_BAR_BLOCK_SIZE,
    opacity: 1,
    pointerEvents: 'auto',
  }

  const fullScreenStyle: CSSObject = {
    justifyContent: 'center',
  }

  return {
    ...base,
    ...(isVisible ? visibleStyle : {}),
    ...(isFullScreen ? fullScreenStyle : {}),
    borderBlockStartColor: isVisible
      ? Color[theme].Surface.N300
      : 'transparent',
    backgroundColor: isFullScreen
      ? Color[theme].Surface.N100
      : Color[theme].Surface.N000,
  }
}

export const saveArea = ({
  theme,
  isFullScreen,
  hasError,
}: WithRequiredTheme &
  FullScreenStyleProps & { hasError: boolean }): CSSObject => ({
  alignItems: 'center',
  color: hasError
    ? Color[theme].Text.Warning
    : isFullScreen
      ? Color[theme].Text.Default
      : Color[theme].Text.Gray,
  display: 'flex',
  fontSize: '12px',
  gap: '15px',
  position: isFullScreen ? 'absolute' : undefined,
  insetInlineEnd: isFullScreen ? 0 : undefined,
})

export const buttonGroups: CSSObject = {
  paddingBlock: 14,
}

export const popoverGroup: CSSObject = {
  border: 'none',
  display: 'contents',
}

export const button = ({ theme }: WithRequiredTheme): CSSObject => ({
  inlineSize: '24px',
  blockSize: '24px',
  color: Color[theme].Text.Default,

  '&[aria-pressed="true"]': {
    backgroundColor:
      theme === 'light' ? Overlay.BL200['16'] : Overlay.BD200['24'],
    color: Color[theme].Text.Default,
  },
  '&:not([aria-pressed="true"])': {
    backgroundColor: 'transparent',
  },
  '&:hover&:not([aria-pressed="true"])&:not(:disabled)&:not([aria-disabled="true"])':
    {
      backgroundColor:
        theme === 'light' ? Overlay.BL200['08'] : Overlay.BD200['16'],
    },

  '.lapidary-icon': {
    blockSize: '16px',
    inlineSize: '16px',
  },
  svg: {
    blockSize: 'inherit',
    inlineSize: 'inherit',
  },
})

export const viewModeSelectWrapper = ({
  isNarrow,
}: ViewModeWrapperWidth): CSSObject => ({
  insetBlockEnd: isNarrow ? 0 : 14,
  insetInlineStart: isNarrow ? 0 : 20,
  position: isNarrow ? 'relative' : 'absolute',
  zIndex: ZIndex.Element.Level2,
})

export const viewModeSelectOverrides = ({
  theme,
}: WithRequiredTheme): CSSObject => ({
  visibility: 'visible',
  button: {
    gap: 6,
    blockSize: 24,
    backgroundColor: Color[theme].Surface.N000,
  },

  '> div': {
    insetBlockEnd: 26,
    position: 'absolute',
  },
})
